import AdjustmentsHorizontalIcon from '@heroicons/react/24/outline/AdjustmentsHorizontalIcon';
import MagnifyingGlassIcon from '@heroicons/react/24/outline/MagnifyingGlassIcon';
import TagIcon from '@heroicons/react/24/outline/TagIcon';
import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import { ComponentPropsWithoutRef, ComponentType, ReactNode } from 'react';

import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Input } from '@/components/core/Input/Input';
import { useTranslation } from '@/hooks/useTranslation';

interface DamLibrarySearchRowProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    activeFilterCount: number;
    activeTagCount: number;
    /** Opens the filter panel; the button stays disabled while no handler is wired. */
    onOpenFilter?: () => void;
    /** Opens the tags panel; the button stays disabled while no handler is wired. */
    onOpenTags?: () => void;
    /** Active filter chips below the row. */
    children?: ReactNode;
}

interface PanelButtonProps {
    label: string;
    icon: ComponentType<ComponentPropsWithoutRef<'svg'>>;
    count: number;
    onClick?: () => void;
}

function PanelButton({ label, icon, count, onClick }: PanelButtonProps) {
    return (
        <span className="tw-relative tw-inline-flex tw-shrink-0">
            <Button variant="outline" hasIcon icon={icon} disabled={!onClick} onClick={onClick}>
                {label}
            </Button>
            {count > 0 && (
                <Badge type="count" variant="error" className="tw-absolute tw--right-2 tw--top-2">
                    {count}
                </Badge>
            )}
        </span>
    );
}

/**
 * Search, filter and tags row of the library header (wireframe `I6392:31615;6379:33498;6473:41974`,
 * buttons `…;6474:28842`, `…;7136:189919`) with the active filter chips below.
 */
function DamLibrarySearchRow({
    searchTerm,
    onSearchChange,
    activeFilterCount,
    activeTagCount,
    onOpenFilter,
    onOpenTags,
    children,
}: DamLibrarySearchRowProps) {
    const { t } = useTranslation();

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-2 md:tw-flex-row md:tw-items-center md:tw-justify-center">
                <div className="tw-relative tw-w-full md:tw-max-w-xl">
                    <MagnifyingGlassIcon aria-hidden className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-h-5 tw-w-5 tw--translate-y-1/2 tw-icon-primary" />
                    <Input
                        type="text"
                        value={searchTerm}
                        placeholder={t('DAM.LIBRARY.SEARCH_PLACEHOLDER')}
                        aria-label={t('DAM.LIBRARY.SEARCH_PLACEHOLDER')}
                        onChange={(event) => onSearchChange(event.target.value)}
                        className="tw-pl-10 tw-pr-10"
                    />
                    {searchTerm && (
                        <ButtonPanel
                            isDisabled={false}
                            icon={XMarkIcon}
                            aria-label={t('DAM.LIBRARY.CLEAR_SEARCH')}
                            tooltip={t('DAM.LIBRARY.CLEAR_SEARCH')}
                            onClick={() => onSearchChange('')}
                            className="tw-absolute tw-right-0.5 tw-top-1/2 tw-h-8 tw-w-8 tw--translate-y-1/2 lg:tw-h-8 lg:tw-w-8"
                        />
                    )}
                </div>
                <div className="tw-flex tw-gap-2">
                    <PanelButton label={t('DAM.LIBRARY.FILTER')} icon={AdjustmentsHorizontalIcon} count={activeFilterCount} onClick={onOpenFilter} />
                    <PanelButton label={t('DAM.LIBRARY.TAGS')} icon={TagIcon} count={activeTagCount} onClick={onOpenTags} />
                </div>
            </div>
            {children}
        </div>
    );
}

export { DamLibrarySearchRow };
export type { DamLibrarySearchRowProps };
